import React from 'react';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  variant?: 'default' | 'bordered' | 'elevated';
}

const paddingClasses = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

const variantClasses = {
  default: 'bg-white',
  bordered: 'bg-white border border-gray-200',
  elevated: 'bg-white shadow-sm',
};

export function Card({ children, className = '', padding = 'md', variant = 'elevated' }: CardProps) {
  return (
    <div className={`rounded-xl ${variantClasses[variant]} ${paddingClasses[padding]} ${className}`}>
      {children}
    </div>
  );
}

interface StatsCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  trend?: string;
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  action?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
}

const statsVariantClasses = {
  default: { bg: 'bg-gray-50', icon: 'bg-gray-100 text-gray-600' },
  primary: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600' },
  success: { bg: 'bg-green-50', icon: 'bg-green-100 text-green-600' },
  warning: { bg: 'bg-amber-50', icon: 'bg-amber-100 text-amber-600' },
  danger: { bg: 'bg-red-50', icon: 'bg-red-100 text-red-600' },
};

export function StatsCard({
  title,
  value,
  icon,
  trend,
  variant = 'default',
  action,
  className = '',
}: StatsCardProps) {
  const variantStyles = statsVariantClasses[variant];

  return (
    <Card className={className}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900">{value}</p>
            {trend && <span className="text-sm text-gray-500">{trend}</span>}
          </div>
          {action && (
            <button
              onClick={action.onClick}
              className="text-sm text-blue-600 hover:text-blue-700 font-medium mt-2 hover:underline"
            >
              {action.label}
            </button>
          )}
        </div>
        {icon && (
          <div className={`p-3 rounded-xl ${variantStyles.icon}`}>
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
}
